import { Box, BoxProps, Flex, Link } from '@chakra-ui/react';
import { isEmpty } from 'lodash';
import { useCallback, useMemo } from 'react';
import { match } from 'ts-pattern';
import { ContentCardPlatformIcon } from '~/components/ContentCard/components/ContentCardPlatformIcon';
import { FallbackImage } from '~/components/FallbackImage';
import { VideoHover } from '~/components/VideoHover';
import { SOCIAL_PLATFORMS } from '~/constants';
import { getAssetDimensions } from '~/contentSearchUtils';
import { calculateProportionalWidth } from '~/core/utils/image';
import { ContentImage } from '~/screens/SearchContent/components/ContentImage';
import { ASSET_TYPE, ISearchContentResult } from '~/searchApi/content';
import { CONTENT_TYPE, ICreatorAgencyContentSelected } from '~/types';
import { convertPxToRems } from '~/utils-ts';

import { CarouselNotClicableItem } from './CarouselNotClicableItem';

interface Props extends BoxProps {
  content:
    | (ICreatorAgencyContentSelected & {
        created_time?: string;
      })
    | (ISearchContentResult & {
        created_time?: string;
      });
  showPlatformIcon?: boolean;
}

const maxHeight = 200;

export const CarouselItem: React.FC<Props> = ({
  content,
  showPlatformIcon,
}) => {
  const {
    assets,
    caption,
    platform,
    shareUrl,
    externalUrl,
    networkId,
    publishedAt,
    created_time,
  } = content;

  const isStoryExpired = useMemo(() => {
    if (
      content.platform === SOCIAL_PLATFORMS.instagram &&
      (content.channel === CONTENT_TYPE.Story ||
        // @ts-ignore - inconsistent api :\
        content.product_type?.toLowerCase() === CONTENT_TYPE.Story)
    ) {
      const hoursInMilliseconds = 24 * 60 * 60 * 1000;
      const currentDate = new Date();
      const createDate = new Date(
        publishedAt ??
          created_time ??
          // @ts-ignore - inconsistent api :\
          content.published_at ??
          // @ts-ignore - inconsistent api :\
          content.created_at,
      );

      return currentDate.getTime() - createDate.getTime() > hoursInMilliseconds;
    }
    return false;
  }, [content, created_time, publishedAt]);

  const [
    {
      previewUrl = undefined,
      url = '',
      type = null,
      thumbnail = undefined,
    } = {},
  ] = assets;

  const { height, width } = getAssetDimensions({
    assets,
    platform,
  });

  const contentLink = {
    // @ts-ignore - types are not consistent between apis :(
    [SOCIAL_PLATFORMS.instagram]: shareUrl || content.share_url,
    // @ts-ignore - types are not consistent between apis :(
    [SOCIAL_PLATFORMS.tiktok]: externalUrl || content.external_url,
    [SOCIAL_PLATFORMS.youtube]: ` https://www.youtube.com/watch?v=${
      // @ts-ignore - types are not consistent between apis :(
      networkId || content.network_id
    }`,
  };

  const proportionalWidth = useMemo(
    () => calculateProportionalWidth({ height, width, maxHeight }),
    [height, width],
  );

  const stopPropagation = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <Box
      data-testid="carousel-item"
      position="relative"
      maxHeight={convertPxToRems(200)}
      height={convertPxToRems(200)}
      width={proportionalWidth}
      marginBottom={6}
      marginRight={4}
      data-channel={platform}
      sx={{
        '::before': {
          display: 'block',
          paddingBottom: `${(height / width) * 100}%`,
          content: '""',
        },
      }}
      onClick={stopPropagation}
    >
      {isStoryExpired ? (
        <CarouselNotClicableItem
          url={url}
          type={type}
          previewUrl={previewUrl || thumbnail}
          caption={caption}
        />
      ) : (
        <Box
          position="absolute"
          top="0"
          left="0"
          width="100%"
          height="100%"
          overflow="hidden"
          borderRadius="lg"
        >
          <Link href={contentLink[platform]} isExternal>
            {match(type?.toLowerCase())
              .with(ASSET_TYPE.video, () => (
                <VideoHover
                  src={url}
                  poster={previewUrl || thumbnail}
                  width="100%"
                  height="100%"
                  objectFit="cover"
                  containerProps={{
                    height: '100%',
                  }}
                  fallback={
                    <FallbackImage
                      width="100%"
                      height="100%"
                      objectFit="cover"
                      iconProps={{ boxSize: 8 }}
                    />
                  }
                />
              ))
              .otherwise(() => (
                <ContentImage
                  position="absolute"
                  draggable="false"
                  width="100%"
                  height="100%"
                  top={0}
                  src={isEmpty(url) ? previewUrl || thumbnail : url}
                  alt={caption}
                  objectFit="cover"
                  background="white"
                  fallbackImageProps={{
                    width: '100%',
                    height: '100%',
                    iconProps: {
                      boxSize: 8,
                    },
                  }}
                />
              ))}
          </Link>
        </Box>
      )}
      {showPlatformIcon && (
        <Flex
          position="absolute"
          top="100%"
          gap={1}
          alignItems="center"
          marginTop={2}
          marginLeft={1}
        >
          <ContentCardPlatformIcon width={4} height={4} platform={platform} />
        </Flex>
      )}
    </Box>
  );
};
